'use client';

import { FileText, Eye, Download, Trash2 } from '@/components/ui/icons';
import { cn } from '@/lib/utils';

interface Document {
  id: string;
  name: string;
  size: string;
  uploadedAt: string;
  uploadedBy: string;
  status: 'processed' | 'processing' | 'pending' | 'failed';
  chunks?: number;
  type: 'pdf' | 'docx' | 'zip' | 'txt' | 'other';
}

// Icon component for different file types
function FileIcon({ type, className }: { type: Document['type']; className?: string }) {
  // PDF icon - red
  if (type === 'pdf') {
    return (
      <div className={cn('w-5 h-5 flex items-center justify-center', className)}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="1" width="14" height="18" rx="2" fill="#ef4444" fillOpacity="0.1" stroke="#ef4444" strokeWidth="1.5"/>
          <path d="M7 7h6M7 10h6M7 13h4" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </div>
    );
  }
  
  // Default file icon
  return <FileText className={cn('w-5 h-5 text-gray-400', className)} />;
}

interface DocumentRowProps {
  document: Document;
  onView?: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
}

export function DocumentRow({ document, onView, onDownload, onDelete }: DocumentRowProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-[10px] p-4">
      <div className="flex items-start gap-4">
        {/* File Icon */}
        <div className="mt-1">
          <FileIcon type={document.type} />
        </div>

        {/* Document Info */}
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              {/* Document Name */}
              <h4 className="text-base font-normal text-gray-900 tracking-tight">
                {document.name}
              </h4>
              
              {/* Meta Info */}
              <div className="flex items-center gap-2 mt-1 text-base text-gray-500">
                <span>{document.size}</span>
                <span>•</span>
                <span>Uploaded {document.uploadedAt}</span>
                <span>•</span>
                <span>By {document.uploadedBy}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={onView}
                className="w-9 h-9 flex items-center justify-center rounded-[10px] hover:bg-gray-100 transition-colors"
                title="View"
              >
                <Eye className="w-5 h-5 text-gray-500" />
              </button>
              <button
                onClick={onDownload}
                className="w-9 h-9 flex items-center justify-center rounded-[10px] hover:bg-gray-100 transition-colors"
                title="Download"
              >
                <Download className="w-5 h-5 text-gray-500" />
              </button>
              <button
                onClick={onDelete}
                className="w-9 h-9 flex items-center justify-center rounded-[10px] hover:bg-gray-100 transition-colors"
                title="Delete"
              >
                <Trash2 className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Status Badge */}
          <div className="mt-3">
            {document.status === 'processed' ? (
              <span className="inline-flex items-center gap-2 px-2 py-1 bg-green-50 text-green-700 rounded text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Processed • {document.chunks} {document.chunks === 1 ? 'chunk' : 'chunks'}
              </span>
            ) : document.status === 'failed' ? (
              <span className="inline-flex items-center gap-2 px-2 py-1 bg-red-50 text-red-700 rounded text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                Failed to process
              </span>
            ) : document.status === 'pending' ? (
              <span className="inline-flex items-center gap-2 px-2 py-1 bg-gray-50 text-gray-600 rounded text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                Pending processing
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 px-2 py-1 bg-yellow-50 text-yellow-700 rounded text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />
                Processing...
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export type { Document };

